import type { ComponentProps, RefObject } from 'react';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery } from '~/hooks/useMediaQuery';
import { breakpoints } from '~/styles/media.css';
import { clsx } from '~/styles/utils';

import { Button } from '../Button/Button';
import { Field } from '../Form/Field/Field';
import { Select } from '../Form/Select/Select';
import {
  activeStyle,
  ellipsisStyle,
  navigationStyle,
  pageNumberStyle,
  pageSizeStyle,
  paginationStyle,
  previousNextStyle,
} from './Pagination.css';
import { getPageNavigationOptions } from './Pagination.utils';

export type PaginationQuery = { pageIndex: number; pageSize: number };

type Props = Omit<ComponentProps<'div'>, 'onChange'> & {
  onChange: (query: PaginationQuery) => void;
  pageIndex: number;
  pageSize: number;
  /** The start of the paginated content, scrolled into view on navigation. Scrolls to the top of the page when absent. */
  scrollTargetRef?: RefObject<HTMLElement | null>;
  showPageSizeOptions?: boolean;
  totalResults: number;
};

export const Pagination = ({
  onChange,
  pageIndex,
  pageSize,
  scrollTargetRef,
  showPageSizeOptions = false,
  totalResults,
  ...delegated
}: Props) => {
  const { t } = useTranslation(['common']);
  const isMobile = useMediaQuery(breakpoints.small);

  const pageCount = pageSize > 0 ? Math.ceil(totalResults / pageSize) : 0;
  const pageNavigationOptions = getPageNavigationOptions(pageCount, pageIndex);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex + 1 < pageCount;

  useEffect(() => {
    if (pageCount > 0 && pageIndex >= pageCount) {
      onChange({ pageIndex: pageCount - 1, pageSize });
    }
  }, [onChange, pageCount, pageIndex, pageSize]);

  const pageSizeOptions = [10, 20, 30, 40, 50].filter((option) => option === 10 || totalResults > option - 10);

  const pageSizeItems = pageSizeOptions.map((option) => ({
    value: option,
    label: `${t('common:datatable.show')} ${option === pageSizeOptions.at(-1) && totalResults <= 50 ? t('common:datatable.all') : option}`,
  }));

  const showNavigation = pageCount > 1;
  const showPageSize = showPageSizeOptions && pageSizeItems.length > 1;

  if (!showNavigation && !showPageSize) return null;

  const scrollToStart = () => {
    if (!scrollTargetRef) {
      window.scrollTo(0, 0);
      return;
    }

    const scrollTarget = scrollTargetRef.current;
    if (scrollTarget === null) return;

    // Only scroll when the start of the content is hidden behind the sticky header or above the viewport
    const stickyOffset = isMobile ? 110 : 180;
    const { top } = scrollTarget.getBoundingClientRect();
    if (top >= stickyOffset) return;

    window.scrollTo({ top: window.scrollY + top - stickyOffset });
  };

  const changePageSize = (newPageSize: number) => {
    // Keep the first visible row on screen, like TanStack Table's `setPageSize`
    const newPageIndex = Math.floor(pageIndex * (pageSize / newPageSize));

    onChange({ pageIndex: newPageIndex, pageSize: newPageSize });
  };

  const goToPage = (nextPageIndex: number) => {
    if (nextPageIndex === pageIndex) return;
    scrollToStart();
    onChange({ pageIndex: nextPageIndex, pageSize });
  };

  return (
    <div {...delegated} className={clsx(paginationStyle, delegated.className)}>
      {showPageSize && (
        <Field.Root className={pageSizeStyle}>
          <Field.Label srOnly>{t('common:pagination.pageSize')}</Field.Label>
          <Select.Root items={pageSizeItems} onValueChange={(value) => value !== null && changePageSize(value)} value={pageSize}>
            <Select.Button placeholder={t('common:datatable.show')} />
            <Select.List>
              {pageSizeItems.map(({ label, value }) => (
                <Select.Item key={value} value={value}>
                  {label}
                </Select.Item>
              ))}
            </Select.List>
          </Select.Root>
        </Field.Root>
      )}

      {showNavigation && (
        <nav aria-label={t('common:pagination.label')} className={navigationStyle}>
          <Button
            aria-label={t('common:pagination.previous')}
            className={previousNextStyle}
            disabled={!canPreviousPage}
            icon="ChevronBackward"
            onClick={() => goToPage(pageIndex - 1)}
          />

          {pageNavigationOptions.map((option, index) =>
            option.type === 'ellipsis' ? (
              <span aria-hidden className={ellipsisStyle} key={`ellipsis-${index}`}>
                ...
              </span>
            ) : (
              <Button
                aria-current={option.page === pageIndex ? 'page' : undefined}
                className={clsx(pageNumberStyle, option.page === pageIndex && activeStyle)}
                emphasis="tertiary"
                key={option.page}
                onClick={() => goToPage(option.page)}
              >
                {option.page + 1}
              </Button>
            ),
          )}

          <Button
            aria-label={t('common:pagination.next')}
            className={previousNextStyle}
            disabled={!canNextPage}
            icon="ChevronForward"
            onClick={() => goToPage(pageIndex + 1)}
          />
        </nav>
      )}
    </div>
  );
};
